import { monitoredAxios } from './monitoredAxios';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface BatchRequest {
  id: string;
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  params?: Record<string, unknown>;
}

interface BatchResponse {
  [key: string]: unknown;
}

// In dev mode, the Next.js dev server compiles API routes on first hit (often
// >10s via Turbopack). 30s gives us headroom for cold compiles without making
// the suite feel slow in prod where 10s is plenty for cached responses.
const DEFAULT_REQUEST_TIMEOUT_MS = process.env.NODE_ENV === 'development' ? 30000 : 10000;
const DEFAULT_BATCH_TIMEOUT_MS = process.env.NODE_ENV === 'development' ? 60000 : 30000;

export const apiRequest = async <T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  endpoint: string,
  data?: unknown,
  params?: Record<string, unknown>,
  portfolioId?: string,
  timeout: number = DEFAULT_REQUEST_TIMEOUT_MS
): Promise<T> => {
  try {
    const requestParams = portfolioId ? { ...params, portfolioId } : params;
    const response = await monitoredAxios({
      method,
      url: `/api${endpoint}`,
      data,
      params: requestParams,
      timeout,
    });

    return response.data;
  } catch (error: any) {
    console.warn(`API Error (${method} ${endpoint}):`, error);
    throw error;
  }
};

export const batchApiRequest = async (
  requests: BatchRequest[],
  portfolioId?: string,
  timeout: number = DEFAULT_BATCH_TIMEOUT_MS
): Promise<BatchResponse> => {
  try {
    const config = portfolioId
      ? { timeout, params: { portfolioId } as Record<string, unknown> }
      : { timeout };
    const response = await monitoredAxios.post('/api/batch', {
      requests,
    }, config);

    return response.data?.results || {};
  } catch (error: any) {
    console.warn('Batch API Error:', error);
    throw error;
  }
};

export const dashboardApi = {
  getComprehensive: async (scenarioId?: string, includeDefault?: boolean, portfolioId?: string) => {
    const params: Record<string, unknown> = {};
    if (scenarioId) {
      params.scenario_id = scenarioId;
      params.include_default = includeDefault ? 'true' : 'false';
    }

    return apiRequest('GET', '/dashboard/comprehensive', undefined, params, portfolioId, 15000);
  },

  getOverview: async (scenarioId?: string, portfolioId?: string) => {
    const params = scenarioId ? { scenario_id: scenarioId } : undefined;
    return apiRequest('GET', '/dashboard/overview', undefined, params, portfolioId);
  },

  getAssetProjection: async (years: number = 30, scenarioId?: string, portfolioId?: string) => {
    const requests: BatchRequest[] = [
      {
        id: 'asset-projection',
        endpoint: '/dashboard/asset-projection',
        params: { years, ...(scenarioId && { scenario_id: scenarioId }) }
      }
    ];

    const results = await batchApiRequest(requests, portfolioId);
    return results['asset-projection'];
  },

  getFinancialSummary: async (scenarioId?: string, projectionYears?: number, portfolioId?: string) => {
    const params = { ...(scenarioId && { scenario_id: scenarioId }), ...(projectionYears && { years: projectionYears }) };
    return apiRequest('GET', '/dashboard/financial-summary', undefined, params, portfolioId);
  },

  getRecommendations: async (portfolioId?: string) => {
    return apiRequest('GET', '/dashboard/recommendations', undefined, undefined, portfolioId);
  },

  getChartData: async (years: number, scenarioId?: string, includeDefault?: boolean, portfolioId?: string) => {
    const requests: BatchRequest[] = [{
      id: 'chart-data',
      endpoint: '/dashboard/chart-data',
      params: {
        years,
        scenario_id: scenarioId,
        include_default: includeDefault
      }
    }];

    const results = await batchApiRequest(requests, portfolioId);
    return results['chart-data'];
  },

  refresh: async (portfolioId?: string) => {
    return apiRequest('POST', '/dashboard/refresh', undefined, undefined, portfolioId);
  },
};

export const assetsApi = {
  getAll: async (portfolioId?: string) => {
    return apiRequest('GET', '/assets', undefined, undefined, portfolioId);
  },

  getCategories: async (portfolioId?: string) => {
    return apiRequest('GET', '/assets/categories', undefined, undefined, portfolioId);
  },

  getHistoricValues: async (portfolioId?: string) => {
    return apiRequest('GET', '/assets/historic-values/all', undefined, undefined, portfolioId);
  },

  create: async (assetData: unknown, portfolioId?: string) => {
    return apiRequest('POST', '/assets', assetData, undefined, portfolioId);
  },

  update: async (id: string, assetData: unknown, portfolioId?: string) => {
    return apiRequest('PUT', `/assets/${id}`, assetData, undefined, portfolioId);
  },

  delete: async (id: string, portfolioId?: string) => {
    return apiRequest('DELETE', `/assets/${id}`, undefined, undefined, portfolioId);
  },
};

export const incomeApi = {
  getAll: async (scenarioId?: string, portfolioId?: string) => {
    const params = scenarioId ? { scenario_id: scenarioId } : undefined;
    return apiRequest('GET', '/income', undefined, params, portfolioId);
  },

  create: async (incomeData: unknown, portfolioId?: string) => {
    return apiRequest('POST', '/income', incomeData, undefined, portfolioId);
  },

  update: async (id: string, incomeData: unknown, portfolioId?: string) => {
    return apiRequest('PUT', `/income/${id}`, incomeData, undefined, portfolioId);
  },

  delete: async (id: string, portfolioId?: string) => {
    return apiRequest('DELETE', `/income/${id}`, undefined, undefined, portfolioId);
  },
};

export const expensesApi = {
  getAll: async (scenarioId?: string, portfolioId?: string) => {
    const params = scenarioId ? { scenario_id: scenarioId } : undefined;
    return apiRequest('GET', '/expenses', undefined, params, portfolioId);
  },

  create: async (expenseData: unknown, portfolioId?: string) => {
    return apiRequest('POST', '/expenses', expenseData, undefined, portfolioId);
  },

  update: async (id: string, expenseData: unknown, portfolioId?: string) => {
    return apiRequest('PUT', `/expenses/${id}`, expenseData, undefined, portfolioId);
  },

  delete: async (id: string, portfolioId?: string) => {
    return apiRequest('DELETE', `/expenses/${id}`, undefined, undefined, portfolioId);
  },
};

export const transactionsApi = {
  getAll: async (portfolioId?: string) => {
    return apiRequest('GET', '/transactions', undefined, undefined, portfolioId);
  },

  create: async (transactionData: unknown, portfolioId?: string) => {
    return apiRequest('POST', '/transactions', transactionData, undefined, portfolioId);
  },

  update: async (id: string, transactionData: unknown, portfolioId?: string) => {
    return apiRequest('PUT', `/transactions/${id}`, transactionData, undefined, portfolioId);
  },

  delete: async (id: string, portfolioId?: string) => {
    return apiRequest('DELETE', `/transactions/${id}`, undefined, undefined, portfolioId);
  },
};

export const portfolioSettingsApi = {
  get: async (portfolioId?: string) => {
    return apiRequest('GET', '/portfolio-settings', undefined, undefined, portfolioId);
  },

  update: async (settingsData: unknown, portfolioId?: string) => {
    return apiRequest('PUT', '/portfolio-settings', settingsData, undefined, portfolioId);
  },
};

export const extractErrorMessage = (error: any, defaultMessage: string = 'An error occurred') => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return defaultMessage;
};

export const isNetworkError = (error: any) => {
  return !error.response && error.code === 'NETWORK_ERROR';
};

export const isAuthError = (error: any) => {
  return error.response?.status === 401 || error.response?.status === 403;
};
